import { useTranslation } from 'react-i18next'
import styled, { css, useTheme } from 'styled-components'

import { H4 } from 'js/components/Heading'
import HideVisually from 'js/components/HideVisually/HideVisually'
import Spacer from 'js/components/Spacer/Spacer'
import Text from 'js/components/Text/Text'
import Tooltip from 'js/components/Tooltip/Tooltip'

import WarnIcon from 'icons/warn.svg'

interface WrapperProps extends React.HTMLAttributes<HTMLDivElement> {
    $prependChildren: boolean
}

const Wrapper = styled.div<WrapperProps>`
    ${(p) =>
        p.$prependChildren &&
        css`
            display: grid;
            grid-template-columns: auto 1fr;
            grid-template-areas:
                'a b'
                'c d'
                'e f';
            grid-column-gap: 8px;

            .FormItemTitle {
                grid-area: b;
            }

            .FormItemDescription {
                grid-area: d;
            }

            .FormItemErrorAndHelper {
                grid-area: f;
            }
        `}

    ${(p) =>
        !p.$prependChildren &&
        css`
            display: flex;
            flex-direction: column;

            .FormItemTitle {
                order: -2;
                margin-bottom: 8px;
            }

            .FormItemTitle + .FormItemDescription {
                margin-top: -6px;
            }

            .FormItemDescription {
                order: -1;
                margin-bottom: 12px;
            }

            .FormItemErrorAndHelper {
                margin-top: 8px;
            }
        `}

    .FormItemTitle {
        display: flex;
        align-items: center;
    }
`

export interface Props {
    children?: React.ReactNode
    id?: string
    required?: boolean
    title?: string
    titleAs: React.ElementType
    titleAsLabel?: boolean
    description?: string
    helper?: string
    error?: React.ReactNode | string
    prependChildren?: boolean
    ElementAfterTitle?: React.ReactNode
}

const FormItemTemplate = ({
    children,
    id, // More specific id if there can be multiple instances of the same FormItem on screen
    required,
    title,
    titleAs,
    titleAsLabel,
    description, // Text below the title
    helper, // Text below the input field
    error,
    prependChildren,
    ElementAfterTitle,
    ...rest
}: Props) => {
    const { t } = useTranslation()

    const theme = useTheme()

    const Title = titleAs || H4

    return (
        <Wrapper $prependChildren={prependChildren} {...rest}>
            {children}

            {title && (
                <div className="FormItemTitle">
                    <Title
                        as={titleAsLabel ? 'label' : null}
                        htmlFor={titleAsLabel ? id : null}
                    >
                        {title}
                        {required && (
                            <>
                                <HideVisually>
                                    ({t('global.required')})
                                </HideVisually>
                                <Tooltip
                                    theme="warn-white"
                                    placement="right"
                                    content={t('global.required')}
                                >
                                    <span
                                        aria-hidden
                                        style={{
                                            paddingLeft: '2px',
                                            color: theme.color.warn.main,
                                        }}
                                    >
                                        *
                                    </span>
                                </Tooltip>
                            </>
                        )}
                    </Title>
                    {ElementAfterTitle}
                </div>
            )}

            {description && (
                <Text
                    id={`${id}-description`}
                    size="small"
                    variant="grey"
                    className="FormItemDescription"
                >
                    {description}
                </Text>
            )}

            {(error || helper) && (
                <Spacer spacing="tiny" className="FormItemErrorAndHelper">
                    {!!error && (
                        <Text size="small" variant="warn" role="alert">
                            <WarnIcon
                                style={{
                                    display: 'inline-block',
                                    verticalAlign: 'text-top',
                                    marginRight: '5px',
                                }}
                            />
                            {error}
                        </Text>
                    )}
                    {helper && (
                        <Text id={`${id}-helper`} size="small" variant="grey">
                            {helper}
                        </Text>
                    )}
                </Spacer>
            )}
        </Wrapper>
    )
}

export default FormItemTemplate
